import React from 'react';
import {
  AllianceIdentity,
  AlliancePerson,
} from '../components/alliance/alliance-identity';
import { AuthContext } from '../contexts/auth-context';
import { AgentSelect } from '../types';

/** "First Last" from a record's name fields, or null when both are empty */
const joinName = (params: {
  first?: string | null;
  last?: string | null;
  fallback?: string | null;
}) => {
  const name = [params.first, params.last].filter(Boolean).join(' ').trim();
  return name || params.fallback || null;
};

/** An impersonated or authorized agent record as an AlliancePerson */
const agentToPerson = (params: { agent: AgentSelect }): AlliancePerson => {
  // Params
  const { agent } = params;
  const id = agent.AgtNo || '';
  return {
    fullName:
      joinName({
        first: agent.Fname,
        last: agent.Lname,
        fallback: agent.AgtName,
      }) || id,
    id,
    email: agent.Email,
  };
};

/**
 * Builds the normalized AllianceIdentity for the user control and System
 * Screens from Contracting's auth state.
 *
 * - Staff: `myStaffForAgt` on the access token. The staff member's name comes
 *   from /authorize Staff; the agent's name from /authorize Agent when it is
 *   that agent.
 * - Agent: /authorize returned an Agent record.
 * - Admin: everyone else. The name comes from /authorize Staff (the
 *   employee record admins get), then the ID token, then the username.
 *
 * Returns null until the Cognito user is known.
 */
export function useAllianceIdentity(): AllianceIdentity | null {
  // Context
  const { state } = React.useContext(AuthContext);

  const { user, session, authorize, impersonatedAgent } = state;

  return React.useMemo(() => {
    if (!user) {
      return null;
    }

    const username = user.getUsername();
    const idToken = session?.getIdToken().decodePayload() || {};
    const staffAgtNo: string | undefined = session
      ?.getAccessToken()
      .decodePayload()['myStaffForAgt'];
    const tokenName = joinName({
      first: idToken['given_name'],
      last: idToken['family_name'],
      fallback: idToken['name'],
    });
    const tokenEmail: string | null = idToken['email'] || null;

    const impersonating = impersonatedAgent
      ? agentToPerson({ agent: impersonatedAgent })
      : null;

    if (staffAgtNo) {
      const staffName = joinName({
        first: authorize?.Staff?.Fname,
        last: authorize?.Staff?.Lname,
      });
      const agent =
        authorize?.Agent && authorize.Agent.AgtNo === staffAgtNo
          ? agentToPerson({ agent: authorize.Agent })
          : { fullName: staffAgtNo, id: staffAgtNo };
      return {
        role: 'staff',
        user: {
          fullName: staffName || tokenName || username,
          id: authorize?.Staff?.Username || username,
          email: tokenEmail,
        },
        staffFor: agent,
        impersonating,
      };
    }

    if (authorize?.Agent) {
      const agent = agentToPerson({ agent: authorize.Agent });
      return {
        role: 'agent',
        user: { ...agent, email: agent.email || tokenEmail },
        staffFor: null,
        impersonating,
      };
    }

    // Admins: /authorize returns their employee record in Staff (no
    // myStaffForAgt), which carries their name
    const employeeName = joinName({
      first: authorize?.Staff?.Fname,
      last: authorize?.Staff?.Lname,
    });
    return {
      role: 'admin',
      user: {
        fullName: employeeName || tokenName || username,
        id: username,
        email: tokenEmail,
      },
      staffFor: null,
      impersonating,
    };
  }, [user, session, authorize, impersonatedAgent]);
}
